import { useEffect, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PaperPlaneTiltIcon, LightningIcon } from "@phosphor-icons/react";
import AnswerSources from "@/components/AnswerSources";
import RichText from "@/components/RichText";

const SUGGESTIONS = [
  "Who has the best MVP case right now?",
  "Why is the Lakers vs Celtics game must-watch tonight?",
  "Best fantasy waiver-wire pickups this week?",
  "Which rookies have the hottest cards?",
];

export default function Ask() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/chat/history").then((r) => setMessages(r.data || []));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text: msg, language }]);
    setLoading(true);
    try {
      const res = await api.post("/chat", { message: msg, language });
      setMessages((m) => [...m, { id: res.data.id, role: "assistant", text: res.data.text, sources: res.data.sources, language }]);
    } catch (err) {
      const limited = err?.response?.status === 402;
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", text: limited ? "You've used today's 5 free questions — upgrade to Pro Fan for unlimited answers." : "I couldn't reach the broadcast booth. Try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-4 fade-up flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#32D74B] mb-2">Ask Gamly AI</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Talk Sports</h1>
        </div>
        <select
          data-testid="ask-lang"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="bg-black border border-white/15 text-white/80 text-xs uppercase font-bold tracking-wider rounded-md px-3 py-2"
        >
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-md h-[60vh] flex flex-col" data-testid="chat-box">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <LightningIcon weight="fill" size={36} className="text-[#0A84FF] mb-3" />
              <p className="text-white/70 max-w-md text-sm mb-4">
                Ask anything about live games, players, fantasy, or card markets. Tap a starter or type your own.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-2xl">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`ask-suggestion-${s.slice(0, 20).toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-left text-xs text-white/80 px-3 py-2 rounded-md border border-white/10 bg-white/[0.02] hover:border-[#0A84FF]/40 hover:bg-[#0A84FF]/5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`msg-${m.role}`}
                className={`max-w-[80%] px-4 py-3 rounded-md ${
                  m.role === "user"
                    ? "bg-[#0A84FF] text-white"
                    : "bg-black/40 border border-white/10 text-white/90"
                }`}
              >
                <RichText text={m.text} className="text-sm" />
                <AnswerSources sources={m.sources} testId={`ask-sources-${m.id}`} />
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="px-4 py-3 bg-black/40 border border-white/10 rounded-md text-white/60 text-sm font-display tracking-wider">
                Gamly is thinking…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="p-3 border-t border-white/5 flex items-center gap-2"
        >
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a game, player, or card…"
            className="flex-1 bg-black border border-white/15 focus:border-[#0A84FF] focus:ring-1 focus:ring-[#0A84FF] outline-none rounded-md px-4 py-3 text-sm text-white placeholder-white/30"
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={loading || !input.trim()}
            className="px-4 py-3 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white font-bold uppercase tracking-wider text-xs disabled:opacity-40 inline-flex items-center gap-2"
          >
            <PaperPlaneTiltIcon weight="fill" /> Send
          </button>
        </form>
      </div>
    </AppLayout>
  );
}
